import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BarEntry } from '../../components/bar-entry/bar-entry';
import { ListEntry } from '../../components/list-entry/list-entry';
import { BarList } from '../../models/bar-list.model';
import { BarListService } from '../../services/bar-list.service';
import { BarService } from '../../services/bar.service';

@Component({
  selector: 'app-search',
  imports: [CommonModule, FormsModule, BarEntry, ListEntry],
  templateUrl: './search.html',
  styleUrl: './search.scss',
})
export class Search implements OnInit, AfterViewInit {
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;

  searchQuery: string = '';
  showDiscovery: boolean = true;
  isSearching: boolean = false;
  searchType: 'bars' | 'lists' = 'bars';
  searchHistory: string[] = [];
  filteredSuggestions: string[] = [];
  showSuggestions: boolean = false;

  bars: any[] = [];
  lists: any[] = [];
  hasSearched: boolean = false;

  private searchGen = 0;

  constructor(
    private barService: BarService,
    private barListService: BarListService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.loadSearchHistory();
  }

  goHome() {
    this.router.navigate(['/']);
  }

  ngOnInit() {
    this.restoreSearchState();

    // Recuperar query param si existe
    this.route.queryParams.subscribe(params => {
      if (params['q']) {
        this.searchQuery = params['q'];
        this.performSearch();
      }
    });
  }

  ngAfterViewInit() {
    // Poner foco en el input de búsqueda después de que se renderice la vista
    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.focus();
      }
    }, 100);
  }

  onSearchInput(event: any) {
    this.searchQuery = event.target.value;
    this.showSuggestions = true;

    if (this.searchQuery.trim().length === 0) {
      this.showDiscovery = true;
      this.showSuggestions = false;
      this.bars = [];
      this.lists = [];
      this.hasSearched = false;
    } else {
      this.showDiscovery = false;
      this.filteredSuggestions = this.searchHistory
        .filter(item => item.toLowerCase().includes(this.searchQuery.toLowerCase()) && item.toLowerCase() !== this.searchQuery.toLowerCase())
        .slice(0, 5);
    }
  }

  onSearchFocus() {
    if (this.searchQuery.trim().length === 0) {
      this.filteredSuggestions = this.searchHistory;
      this.showSuggestions = true;
    }
  }

  selectSuggestion(suggestion: string) {
    this.searchQuery = suggestion;
    this.showSuggestions = false;
    this.performSearch();
  }

  async performSearch() {
    if (this.searchQuery.trim().length === 0) {
      this.showDiscovery = true;
      return;
    }

    const gen = ++this.searchGen;
    this.isSearching = true;
    this.showDiscovery = false;
    this.showSuggestions = false;
    this.hasSearched = true;
    this.saveSuggestion();
    this.cdr.detectChanges();

    try {
      if (this.searchType === 'bars') {
        const result = await this.barService.findBars(this.searchQuery);
        if (gen !== this.searchGen) return;
        this.bars = result.bars || [];
      } else {
        const result = await this.barListService.findBarLists(this.searchQuery);
        if (gen !== this.searchGen) return;
        this.lists = result.items || [];
      }
    } catch (error) {
      if (gen !== this.searchGen) return;
      console.error('Error searching:', error);
      if (this.searchType === 'bars') {
        this.bars = [];
      } else {
        this.lists = [];
      }
    } finally {
      if (gen === this.searchGen) {
        this.isSearching = false;
        this.cdr.detectChanges();
      }
    }
  }

  onSearchKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.performSearch();
    }
  }

  onSegmentChange(type: 'bars' | 'lists') {
    this.searchType = type;
    if (this.hasSearched && this.searchQuery.trim().length > 0) {
      this.performSearch();
    }
  }

  deleteSuggestion(suggestion: string) {
    const index = this.searchHistory.indexOf(suggestion);
    if (index > -1) {
      this.searchHistory.splice(index, 1);
    }
    localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    this.filteredSuggestions = this.filteredSuggestions.filter(s => s !== suggestion);
  }

  private loadSearchHistory() {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      this.searchHistory = JSON.parse(history);
    }
  }

  private saveSuggestion() {
    const queryLower = this.searchQuery.toLowerCase();
    const index = this.searchHistory.indexOf(queryLower);
    if (index > -1) {
      this.searchHistory.splice(index, 1);
    }
    this.searchHistory.unshift(queryLower);
    if (this.searchHistory.length > 10) {
      this.searchHistory = this.searchHistory.slice(0, 10);
    }
    localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
  }

  onSeeDetail(barId: string) {
    // Guardar posición del scroll antes de navegar
    const scrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
    sessionStorage.setItem('searchScrollPosition', scrollPosition.toString());
    this.saveSearchState();
  }

  onListSelected(list: BarList) {
    const scrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
    sessionStorage.setItem('searchScrollPosition', scrollPosition.toString());
    this.saveSearchState();
    this.router.navigate(['/lists', list.id]);
  }

  saveSearchState() {
    const state = {
      searchQuery: this.searchQuery,
      searchType: this.searchType,
      bars: this.bars,
      lists: this.lists,
      hasSearched: this.hasSearched,
      showDiscovery: this.showDiscovery
    };
    sessionStorage.setItem('searchState', JSON.stringify(state));
  }

  private restoreSearchState() {
    const stateStr = sessionStorage.getItem('searchState');
    if (stateStr) {
      try {
        const state = JSON.parse(stateStr);
        this.searchQuery = state.searchQuery || '';
        this.searchType = state.searchType || 'bars';
        this.bars = state.bars || [];
        this.lists = state.lists || [];
        this.hasSearched = state.hasSearched || false;
        this.showDiscovery = state.showDiscovery !== undefined ? state.showDiscovery : true;

        // Forzar detección de cambios primero
        this.cdr.detectChanges();

        // Si viene de detalle, restaurar la posición del scroll después de renderizar
        const scrollPosition = parseInt(sessionStorage.getItem('searchScrollPosition') || '0', 10);
        setTimeout(() => {
          document.documentElement.scrollTop = scrollPosition;
          document.body.scrollTop = scrollPosition;
        }, 0);

        // Limpiar el estado guardado para evitar restaurarlo en futuras visitas
        sessionStorage.removeItem('searchState');
        sessionStorage.removeItem('searchScrollPosition');
      } catch (error) {
        console.error('Error restoring search state:', error);
      }
    } else {
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  }
}
